import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import Swal from 'sweetalert2';
import html2pdf from 'html2pdf.js';

const IncomingInvoices = () => {
  const { t } = useTranslation();
  const { canView, canCreate, canUpdate, canDelete } = usePermissions();

  // Print styles
  const printStyles = `
    <style>
      @media print {
        @page {
          margin: 0.5in;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        html, body {
          height: auto !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
        .print-modal {
          position: static !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
          background: white !important;
          overflow: visible !important;
          height: auto !important;
          z-index: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          display: block !important;
        }
        .print-content {
          position: static !important;
          top: auto !important;
          left: auto !important;
          margin: 0 !important;
          padding: 20px !important;
          width: 100% !important;
          max-width: none !important;
          box-shadow: none !important;
          border: none !important;
          background: white !important;
          display: block !important;
        }
        .print-hide {
          display: none !important;
        }
        .grid {
          display: block !important;
        }
        .lg\\:grid-cols-2 {
          display: block !important;
        }
        .lg\\:col-span-2 {
          display: block !important;
        }
        .md\\:grid-cols-2 {
          display: block !important;
        }
        .flex {
          display: block !important;
        }
        .justify-between {
          display: block !important;
        }
        .items-center {
          display: block !important;
        }
        .gap-2 {
          margin-bottom: 10px !important;
        }
        .gap-6 {
          margin-bottom: 20px !important;
        }
        .space-y-2 > * + * {
          margin-top: 10px !important;
        }
        .space-y-2 > * {
          margin-bottom: 10px !important;
        }
        .mb-4 {
          margin-bottom: 20px !important;
        }
        .mb-3 {
          margin-bottom: 15px !important;
        }
        .mb-1 {
          margin-bottom: 5px !important;
        }
        .mt-1 {
          margin-top: 5px !important;
        }
        .mt-3 {
          margin-top: 15px !important;
        }
        .mt-6 {
          margin-top: 30px !important;
        }
        .p-4 {
          padding: 20px !important;
        }
        .p-3 {
          padding: 15px !important;
        }
        .rounded-lg {
          border-radius: 8px !important;
        }
        .rounded {
          border-radius: 4px !important;
        }
        .border {
          border: 1px solid #e5e7eb !important;
        }
        .bg-gray-50 {
          background-color: #f9fafb !important;
        }
        .bg-white {
          background-color: white !important;
        }
        .text-gray-700 {
          color: #374151 !important;
        }
        .text-gray-600 {
          color: #4b5563 !important;
        }
        .text-gray-500 {
          color: #6b7280 !important;
        }
        .text-green-600 {
          color: #059669 !important;
        }
        .text-red-600 {
          color: #dc2626 !important;
        }
        .text-blue-600 {
          color: #2563eb !important;
        }
        .text-orange-600 {
          color: #ea580c !important;
        }
        .font-semibold {
          font-weight: 600 !important;
        }
        .font-medium {
          font-weight: 500 !important;
        }
        .text-sm {
          font-size: 14px !important;
        }
        .text-xs {
          font-size: 12px !important;
        }
        .text-lg {
          font-size: 18px !important;
        }
        .text-md {
          font-size: 16px !important;
        }
        .text-2xl {
          font-size: 24px !important;
        }
        /* Table specific styles */
        table {
          border-collapse: collapse !important;
          width: 100% !important;
          margin-bottom: 20px !important;
        }
        th, td {
          border: 1px solid #000 !important;
          padding: 8px !important;
          text-align: left !important;
          font-size: 12px !important;
        }
        th {
          background-color: #f3f4f6 !important;
          font-weight: bold !important;
        }
        tr:nth-child(even) {
          background-color: #f9fafb !important;
        }
        .overflow-x-auto {
          overflow: visible !important;
        }
      }
    </style>
  `;
  const queryClient = useQueryClient();

  // PDF Download function
  const downloadPDF = () => {
    const element = document.querySelector('.print-content');
    if (!element) return;
    
    // Hide buttons before PDF generation
    const buttons = element.querySelectorAll('.print-hide');
    buttons.forEach(btn => {
      btn.style.display = 'none';
    });
    
    // Create a copy of the element for PDF generation
    const pdfElement = element.cloneNode(true);
    
    // Apply PDF-specific styles to the copy
    pdfElement.style.fontSize = '10px';
    pdfElement.style.lineHeight = '1.2';
    pdfElement.style.padding = '10px';
    pdfElement.style.margin = '0';
    
    // Make tables more compact for PDF and convert to single column layout
    const tables = pdfElement.querySelectorAll('table');
    tables.forEach(table => {
      table.style.fontSize = '9px';
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      table.style.marginBottom = '15px';
      
      const cells = table.querySelectorAll('th, td');
      cells.forEach(cell => {
        cell.style.padding = '4px';
        cell.style.fontSize = '9px';
        cell.style.border = '1px solid #000';
        cell.style.wordWrap = 'break-word';
      });
    });
    
    // Convert grid layout to single column for PDF
    const gridContainers = pdfElement.querySelectorAll('.grid');
    gridContainers.forEach(container => {
      container.style.display = 'block';
    });
    
    const gridCols = pdfElement.querySelectorAll('.lg\\:grid-cols-2, .md\\:grid-cols-2');
    gridCols.forEach(col => {
      col.style.display = 'block';
    });
    
    const colSpans = pdfElement.querySelectorAll('.lg\\:col-span-2');
    colSpans.forEach(span => {
      span.style.display = 'block';
    });
    
    // Make sections full width for PDF
    const sections = pdfElement.querySelectorAll('.bg-gray-50');
    sections.forEach(section => {
      section.style.width = '100%';
      section.style.marginBottom = '20px';
      section.style.display = 'block';
    });
    
    // Temporarily add the element to body for PDF generation
    document.body.appendChild(pdfElement);
    
    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `incoming-invoices-statistics-${getCompanyName(detailedStatsCompanyFilter).replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { 
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: 1200
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(pdfElement).save().then(() => {
      // Remove the temporary element
      document.body.removeChild(pdfElement);
      
      // Show buttons again after PDF generation
      buttons.forEach(btn => {
        btn.style.display = '';
      });
    });
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [serialNumberFilter, setSerialNumberFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statsCompanyFilter, setStatsCompanyFilter] = useState('all');
  const [detailedStatsCompanyFilter, setDetailedStatsCompanyFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('bookingDate'); // bookingDate, dueDate, documentDate
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [formData, setFormData] = useState({
    companyId: '',
    serialNumber: '',
    bookingDate: '',
    documentDate: '',
    documentNumber: '',
    customer: '',
    purpose: '',
    grossAmount: '',
    reminderFees: '',
    dueDate: '',
    paymentAmount: '',
    paidOn: '',
    status: 'unpaid'
  });

  // Fetch companies
  const { data: companiesData, isLoading: companiesLoading, error: companiesError } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      console.log('🔍 [COMPANIES] Fetching companies...');
      try {
        const response = await api.get('/companies');
        console.log('🔍 [COMPANIES] Companies response:', response.data);
        console.log('🔍 [COMPANIES] Companies count:', response.data?.length || 0);
        return response.data;
      } catch (error) {
        console.error('❌ [COMPANIES] Error fetching companies:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  });

  // Fetch incoming invoices
  const { data: invoicesData, isLoading, error } = useQuery({
    queryKey: ['incomingInvoices', companyFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyFilter && companyFilter !== 'all') {
        params.append('companyId', companyFilter);
      }
      const response = await api.get(`/incoming-invoices?${params.toString()}`);
      return response.data;
    },
    enabled: true // Geçici olarak her zaman aktif
  });

  const invoices = invoicesData?.invoices || [];

  // Helper function to get company name
  const getCompanyName = (companyId) => {
    if (!companyId || companyId === 'all') return t('incomingInvoices.allCompanies');
    const company = companiesData?.find(c => c._id === companyId);
    return company?.name || t('incomingInvoices.allCompanies');
  };

  // Debug companies data
  useEffect(() => {
    console.log('🔍 [COMPANIES DEBUG] companiesData:', companiesData);
    console.log('🔍 [COMPANIES DEBUG] companiesLoading:', companiesLoading);
    console.log('🔍 [COMPANIES DEBUG] companiesError:', companiesError);
  }, [companiesData, companiesLoading, companiesError]);

  // Statistics query
  const { data: statistics } = useQuery({
    queryKey: ['incomingInvoicesStats', statsCompanyFilter],
    queryFn: async () => {
      console.log('🔍 [FRONTEND] Fetching statistics for company:', statsCompanyFilter);
      const params = new URLSearchParams();
      if (statsCompanyFilter && statsCompanyFilter !== 'all') {
        params.append('companyId', statsCompanyFilter);
      }
      const response = await api.get(`/incoming-invoices/statistics?${params.toString()}`);
      console.log('🔍 [FRONTEND] Statistics response:', response.data);
      return response.data;
    },
    enabled: true
  });

  // Detailed Statistics query (separate from main statistics)
  const { data: detailedStatistics } = useQuery({
    queryKey: ['incomingInvoicesDetailedStats', detailedStatsCompanyFilter],
    queryFn: async () => {
      console.log('🔍 [FRONTEND] Fetching detailed statistics for company:', detailedStatsCompanyFilter);
      const params = new URLSearchParams();
      if (detailedStatsCompanyFilter && detailedStatsCompanyFilter !== 'all') {
        params.append('companyId', detailedStatsCompanyFilter);
      }
      const response = await api.get(`/incoming-invoices/statistics?${params.toString()}`);
      console.log('🔍 [FRONTEND] Detailed statistics response:', response.data);
      return response.data;
    },
    enabled: showDetailModal // Only fetch when modal is open
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/incoming-invoices', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['incomingInvoices']);
      queryClient.invalidateQueries(['incomingInvoicesStats']);
      setShowAddForm(false);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('incomingInvoices.createdSuccessfully'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('incomingInvoices.createFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/incoming-invoices/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['incomingInvoices']);
      queryClient.invalidateQueries(['incomingInvoicesStats']);
      setEditingInvoice(null);
      setShowAddForm(false); // Modal'ı kapat
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('incomingInvoices.updatedSuccessfully'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('incomingInvoices.updateFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/incoming-invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['incomingInvoices']);
      queryClient.invalidateQueries(['incomingInvoicesStats']);
      Swal.fire({
        icon: 'success',
        title: t('incomingInvoices.deletedSuccessfully'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('incomingInvoices.deleteFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  const resetForm = () => {
    setFormData({
      companyId: '',
      serialNumber: '',
      bookingDate: '',
      documentDate: '',
      documentNumber: '',
      customer: '',
      purpose: '',
      grossAmount: '',
      reminderFees: '',
      dueDate: '',
      paymentAmount: '',
      paidOn: '',
      status: 'unpaid'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Handle status changes
      if (name === 'status') {
        if (value === 'paid') {
          // If status is changed to 'paid', set paymentAmount to totalAmount
          const grossAmount = parseFloat(prev.grossAmount) || 0;
          const reminderFees = parseFloat(prev.reminderFees) || 0;
          const totalAmount = grossAmount + reminderFees;
          newData.paymentAmount = totalAmount.toString();
        } else if (value === 'unpaid' || value === 'cancelled') {
          // If status is changed to 'unpaid' or 'cancelled', clear paymentAmount
          newData.paymentAmount = '';
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Calculate total amount
    const grossAmount = parseFloat(formData.grossAmount) || 0;
    const reminderFees = parseFloat(formData.reminderFees) || 0;
    const totalAmount = grossAmount + reminderFees;

    // If status is paid, set paymentAmount to totalAmount
    let paymentAmount = parseFloat(formData.paymentAmount) || 0;
    if (formData.status === 'paid') {
      paymentAmount = totalAmount;
    } else if (formData.status === 'unpaid' || formData.status === 'cancelled') {
      paymentAmount = 0;
    }

    const dataToSubmit = {
      ...formData,
      grossAmount,
      reminderFees,
      totalAmount,
      paymentAmount
    };

    if (editingInvoice) {
      updateMutation.mutate({ id: editingInvoice._id, data: dataToSubmit });
    } else {
      createMutation.mutate(dataToSubmit);
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    
    // Helper function to format date for input
    const formatDateForInput = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    };
    
    setFormData({
      companyId: invoice.companyId || '',
      serialNumber: invoice.serialNumber || '',
      bookingDate: formatDateForInput(invoice.bookingDate),
      documentDate: formatDateForInput(invoice.documentDate),
      documentNumber: invoice.documentNumber || '',
      customer: invoice.customer || '',
      purpose: invoice.purpose || '',
      grossAmount: invoice.grossAmount || '',
      reminderFees: invoice.reminderFees || '',
      dueDate: formatDateForInput(invoice.dueDate),
      paymentAmount: invoice.paymentAmount || '',
      paidOn: formatDateForInput(invoice.paidOn),
      status: invoice.status || 'unpaid'
    });
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('incomingInvoices.confirmDelete'),
      text: t('incomingInvoices.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('incomingInvoices.yesDelete'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingInvoice(null);
    resetForm();
  };

  // Filter and sort invoices based on search term, serial number, status, company and sort options
  const filteredInvoices = invoices
    .filter(invoice => {
      const matchesSearch = invoice.documentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           invoice.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           invoice.purpose?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSerialNumber = !serialNumberFilter || 
                                 invoice.serialNumber?.toLowerCase().includes(serialNumberFilter.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
      
      const matchesCompany = companyFilter === 'all' || invoice.companyId?._id === companyFilter;
      
      return matchesSearch && matchesSerialNumber && matchesStatus && matchesCompany;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      // Get values based on sort field
      switch(sortBy) {
        case 'dueDate':
          aValue = a.dueDate ? new Date(a.dueDate) : new Date(0);
          bValue = b.dueDate ? new Date(b.dueDate) : new Date(0);
          break;
        case 'documentDate':
          aValue = a.documentDate ? new Date(a.documentDate) : new Date(0);
          bValue = b.documentDate ? new Date(b.documentDate) : new Date(0);
          break;
        case 'bookingDate':
        default:
          aValue = a.bookingDate ? new Date(a.bookingDate) : new Date(0);
          bValue = b.bookingDate ? new Date(b.bookingDate) : new Date(0);
          break;
      }
      
      // Sort based on order
      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, serialNumberFilter, statusFilter, companyFilter]);

  if (!canView('incomingInvoices')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('common.accessDenied')}</h1>
          <p className="text-gray-600">{t('common.noPermission')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{t('common.error')}</h1>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('incomingInvoices.title')}</h1>
        {true && ( // Geçici olarak her zaman göster
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <span className="mr-2">+</span>
            {t('incomingInvoices.addNew')}
          </button>
        )}
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="mb-8">
          {/* Company Filter for Statistics */}
          <div className="mb-4 bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700">
                📊 {t('incomingInvoices.statistics')}
              </h3>
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">
                  {t('companies.company')}:
                </label>
                <select
                  value={statsCompanyFilter}
                  onChange={(e) => setStatsCompanyFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{t('incomingInvoices.allCompanies')}</option>
                  {companiesData?.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Basic Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.unpaid')}</h3>
              <p className="text-2xl font-bold text-red-600">{statistics.unpaid || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.paid')}</h3>
              <p className="text-2xl font-bold text-green-600">{statistics.paid || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.cancelled')}</h3>
              <p className="text-2xl font-bold text-gray-600">{statistics.cancelled || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.totalAmount')}</h3>
              <p className="text-2xl font-bold text-blue-600">€{statistics.totalAmount?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.paymentAmount')}</h3>
              <p className="text-2xl font-bold text-green-500">€{statistics.paymentAmount?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('incomingInvoices.outstandingAmount')}</h3>
              <p className="text-2xl font-bold text-orange-600">€{statistics.outstandingAmount?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
          
          {/* Detailed Statistics Button */}
          <div className="text-center">
            <button
              onClick={() => setShowDetailModal(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              📊 {t('incomingInvoices.detailedStatistics')}
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.search')}
            </label>
            <input
              type="text"
              placeholder={t('incomingInvoices.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.serialNumber')}
            </label>
            <input
              type="text"
              placeholder={t('incomingInvoices.serialNumber')}
              value={serialNumberFilter}
              onChange={(e) => setSerialNumberFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('incomingInvoices.allStatuses')}</option>
              <option value="unpaid">{t('incomingInvoices.unpaid')}</option>
              <option value="paid">{t('incomingInvoices.paid')}</option>
              <option value="cancelled">{t('incomingInvoices.cancelled')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('companies.company')}
            </label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={companiesLoading}
            >
              <option value="all">{t('incomingInvoices.allCompanies')}</option>
              {companiesError ? (
                <option value="" disabled>{t('common.error')}</option>
              ) : (
                companiesData?.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.sortBy')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="bookingDate">{t('incomingInvoices.bookingDate')}</option>
              <option value="documentDate">{t('incomingInvoices.documentDate')}</option>
              <option value="dueDate">{t('incomingInvoices.dueDate')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.sortOrder')}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="desc">{t('incomingInvoices.newest')}</option>
              <option value="asc">{t('incomingInvoices.oldest')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('incomingInvoices.itemsPerPage')}
            </label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {t('incomingInvoices.showing')} {startIndex + 1}-{Math.min(endIndex, filteredInvoices.length)} {t('incomingInvoices.of')} {filteredInvoices.length}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-semibold mb-4">
            {editingInvoice ? t('incomingInvoices.editInvoice') : t('incomingInvoices.addNew')}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.company')}
              </label>
              <select
                name="companyId"
                value={formData.companyId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={companiesLoading}
              >
                <option value="">{companiesLoading ? t('common.loading') : t('common.selectCompany')}</option>
                {companiesError ? (
                  <option value="" disabled>{t('common.error')}</option>
                ) : (
                  companiesData?.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.serialNumber')} *
              </label>
              <input
                type="text"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. 2024-001"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.bookingDate')} *
              </label>
              <input
                type="date"
                name="bookingDate"
                value={formData.bookingDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.documentDate')} *
              </label>
              <input
                type="date"
                name="documentDate"
                value={formData.documentDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.documentNumber')} *
              </label>
              <input
                type="text"
                name="documentNumber"
                value={formData.documentNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.customer')} *
              </label>
              <input
                type="text"
                name="customer"
                value={formData.customer}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.purpose')}
              </label>
              <input
                type="text"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.grossAmount')} *
              </label>
              <input
                type="number"
                step="0.01"
                name="grossAmount"
                value={formData.grossAmount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.reminderFees')}
              </label>
              <input
                type="number"
                step="0.01"
                name="reminderFees"
                value={formData.reminderFees}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.dueDate')}
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.paymentAmount')}
              </label>
              <input
                type="number"
                step="0.01"
                name="paymentAmount"
                value={formData.paymentAmount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.paidOn')}
              </label>
              <input
                type="date"
                name="paidOn"
                value={formData.paidOn}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('incomingInvoices.status')} *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="unpaid">{t('incomingInvoices.unpaid')}</option>
                <option value="paid">{t('incomingInvoices.paid')}</option>
                <option value="cancelled">{t('incomingInvoices.cancelled')}</option>
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {editingInvoice ? t('common.update') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.bookingDate')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('companies.company')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.documentNumber')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.customer')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.purpose')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.totalAmount')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.dueDate')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('incomingInvoices.status')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedInvoices.map((invoice, index) => (
                <tr key={invoice._id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {invoice.serialNumber || '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invoice.bookingDate ? new Date(invoice.bookingDate).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-32 truncate" title={invoice.companyId?.name || '-'}>
                      {invoice.companyId?.name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-24 truncate" title={invoice.documentNumber}>
                      {invoice.documentNumber}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-32 truncate" title={invoice.customer}>
                      {invoice.customer}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-40 truncate" title={invoice.purpose}>
                      {invoice.purpose || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    €{invoice.totalAmount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t(`incomingInvoices.${invoice.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="text-blue-600 hover:text-blue-900 text-xs"
                        title={t('common.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(invoice._id)}
                        className="text-red-600 hover:text-red-900 text-xs"
                        title={t('common.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-2">📄</div>
            <p className="text-gray-500">{t('incomingInvoices.noInvoices')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {t('incomingInvoices.page')} {currentPage} {t('incomingInvoices.of')} {totalPages}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.first')}
            </button>
            
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.next')}
            </button>
            
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.last')}
            </button>
          </div>
        </div>
      )}

      {/* Detailed Statistics Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 print-modal">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white print-content">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  📊 {t('incomingInvoices.detailedStatistics')} - {getCompanyName(detailedStatsCompanyFilter)}
                </h3>
                <div className="flex gap-2">
                  <select
                    value={detailedStatsCompanyFilter}
                    onChange={(e) => setDetailedStatsCompanyFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 print-hide"
                  >
                    <option value="all">{t('incomingInvoices.allCompanies')}</option>
                    {companiesData?.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={downloadPDF}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 print-hide"
                  >
                    📄 PDF
                  </button>
                  <button
                    onClick={() => {
                      // Create a new window for printing
                      const printWindow = window.open('', '_blank');
                      const printContent = document.querySelector('.print-content').innerHTML;
                      
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>${t('incomingInvoices.detailedStatistics')} - ${getCompanyName(detailedStatsCompanyFilter)}</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 20px; }
                              .company-header { font-size: 18px; font-weight: bold; margin-bottom: 20px; color: #374151; }
                              table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
                              th, td { border: 1px solid #000; padding: 6px; text-align: left; font-size: 11px; }
                              th { background-color: #f3f4f6; font-weight: bold; }
                              tr:nth-child(even) { background-color: #f9fafb; }
                              h3 { color: #374151; margin-bottom: 20px; }
                              h4 { color: #374151; margin-bottom: 15px; }
                              .text-green-600 { color: #059669; }
                              .text-red-600 { color: #dc2626; }
                              .text-gray-600 { color: #4b5563; }
                              .text-blue-600 { color: #2563eb; }
                              .text-orange-600 { color: #ea580c; }
                              .font-semibold { font-weight: 600; }
                              .font-medium { font-weight: 500; }
                              .text-sm { font-size: 14px; }
                              .text-xs { font-size: 12px; }
                              .text-lg { font-size: 18px; }
                              .text-md { font-size: 16px; }
                              .text-2xl { font-size: 24px; }
                              .mb-4 { margin-bottom: 20px; }
                              .mb-3 { margin-bottom: 15px; }
                              .mb-1 { margin-bottom: 5px; }
                              .mt-1 { margin-top: 5px; }
                              .mt-3 { margin-top: 15px; }
                              .mt-6 { margin-top: 30px; }
                              .p-4 { padding: 20px; }
                              .p-3 { padding: 15px; }
                              .rounded-lg { border-radius: 8px; }
                              .rounded { border-radius: 4px; }
                              .border { border: 1px solid #e5e7eb; }
                              .bg-gray-50 { background-color: #f9fafb; width: 100%; margin-bottom: 20px; display: block; }
                              .bg-white { background-color: white; }
                              .overflow-x-auto { overflow: visible; }
                              .grid { display: block !important; }
                              .lg\\:grid-cols-2 { display: block !important; }
                              .md\\:grid-cols-2 { display: block !important; }
                              .lg\\:col-span-2 { display: block !important; }
                            </style>
                          </head>
                          <body>
                            ${printContent}
                          </body>
                        </html>
                      `);
                      
                      printWindow.document.close();
                      printWindow.print();
                      printWindow.close();
                    }}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 print-hide"
                  >
                    🖨️ {t('common.print')}
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 print-hide"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold mb-3 text-gray-700">
                    📅 {t('incomingInvoices.monthlyBreakdown')} ({t('incomingInvoices.last12Months')})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 p-1 text-left text-xs font-semibold">{t('common.month')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.total')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.paid')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.unpaid')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.cancelled')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedStatistics?.monthlyBreakdown?.map((month, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-1 text-xs font-medium">{month.monthName}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs">{month.total}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-green-600">
                              {month.paid} (€{month.paymentAmount?.toFixed(2)})
                            </td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-red-600">
                              {month.unpaid} (€{(month.totalAmount - month.paymentAmount)?.toFixed(2)})
                            </td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-gray-600">
                              {month.cancelled > 0 ? `${month.cancelled} (€${month.cancelledAmount?.toFixed(2) || '0.00'})` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Yearly Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold mb-3 text-gray-700">
                    📊 {t('incomingInvoices.yearlyBreakdown')} ({t('incomingInvoices.last6Years')})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 p-1 text-left text-xs font-semibold">{t('common.year')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.total')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.paid')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.unpaid')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.cancelled')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedStatistics?.yearlyBreakdown?.map((year, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-1 text-xs font-medium">{year.year}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs">{year.total}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-green-600">
                              {year.paid} (€{year.paymentAmount?.toFixed(2)})
                            </td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-red-600">
                              {year.unpaid} (€{(year.totalAmount - year.paymentAmount)?.toFixed(2)})
                            </td>
                            <td className="border border-gray-300 p-1 text-center text-xs text-gray-600">
                              {year.cancelled > 0 ? `${year.cancelled} (€${year.cancelledAmount?.toFixed(2) || '0.00'})` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Upcoming Payments */}
                <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                  <h4 className="text-md font-semibold mb-3 text-gray-700">
                    ⏰ {t('incomingInvoices.upcomingPayments')}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 p-1 text-left text-xs font-semibold">{t('incomingInvoices.serialNumber')}</th>
                          <th className="border border-gray-300 p-1 text-left text-xs font-semibold">{t('incomingInvoices.customer')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.totalAmount')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.dueDate')}</th>
                          <th className="border border-gray-300 p-1 text-center text-xs font-semibold">{t('incomingInvoices.daysUntilDue')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedStatistics?.upcomingPayments?.map((payment, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-1 text-xs font-medium">{payment.serialNumber}</td>
                            <td className="border border-gray-300 p-1 text-xs">{payment.customer}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs font-semibold">€{payment.totalAmount?.toFixed(2)}</td>
                            <td className="border border-gray-300 p-1 text-center text-xs">
                              {new Date(payment.dueDate).toLocaleDateString('de-DE')}
                            </td>
                            <td className={`border border-gray-300 p-1 text-center text-xs ${
                              payment.daysUntilDue < 0 ? 'text-red-600' : 
                              payment.daysUntilDue <= 7 ? 'text-orange-600' : 'text-gray-600'
                            }`}>
                              {payment.daysUntilDue < 0 ? 
                                `${Math.abs(payment.daysUntilDue)} ${t('incomingInvoices.daysOverdue')}` :
                                `${payment.daysUntilDue} ${t('incomingInvoices.daysUntilDue')}`
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomingInvoices;
